const UTM_QUERY_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
] as const;

type UTMQueryKey = (typeof UTM_QUERY_KEYS)[number];

type UTMRecord = Partial<Record<UTMQueryKey, string>>;

const UTM_QUERY_PARAM_ALIASES: Record<
  UTMQueryKey,
  { authKey: keyof UserAttribution; shouldLowercase: boolean }
> = {
  utm_source: { authKey: 'utmSource', shouldLowercase: true },
  utm_medium: { authKey: 'utmMedium', shouldLowercase: true },
  utm_campaign: { authKey: 'utmCampaign', shouldLowercase: true },
  utm_term: { authKey: 'utmTerm', shouldLowercase: false },
  utm_content: { authKey: 'utmContent', shouldLowercase: false },
};

type RawAttributionCookie = {
  utmSource?: unknown;
  utmMedium?: unknown;
  utmCampaign?: unknown;
  utmTerm?: unknown;
  utmContent?: unknown;
  capturedAt?: unknown;
  landingPath?: unknown;
  inferred?: unknown;
  utm_source?: unknown;
  utm_medium?: unknown;
  utm_campaign?: unknown;
  utm_term?: unknown;
  utm_content?: unknown;
  captured_at?: unknown;
  landing_path?: unknown;
};

export type UserAttribution = {
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmTerm?: string;
  utmContent?: string;
  capturedAt?: string;
  landingPath?: string;
  inferred?: boolean;
};

type AttributionOrigin = 'url' | 'cookie' | 'referrer' | 'none';

type InferredAttribution = { source: string; medium: string };

const SKETCHY_PARTNERSHIP_CAMPAIGNS = new Set([
  'oraxsketchy',
  '39614976-ora x sketchy partnership',
]);

const normalizeValue = (value: string, lowercase = false): string => {
  const trimmed = value.trim();
  if (!trimmed) return '';
  return lowercase ? trimmed.toLowerCase() : trimmed;
};

const pickString = (value: unknown, lowercase = false): string | undefined => {
  if (typeof value !== 'string') return undefined;
  const normalized = normalizeValue(value, lowercase);
  return normalized || undefined;
};

const matchesDomain = (host: string, domain: string) =>
  host === domain || host.endsWith(`.${domain}`);

const stripReferrerPrefix = (host: string) =>
  host.replace(/^(?:www|m|l|lm|amp)\./, '');

/**
 * Maps a browser referrer to Ora's standard source/medium attribution pair.
 * This intentionally mirrors the marketing website implementation.
 */
export const classifyReferrer = (
  referrer: string,
  currentHost: string
): InferredAttribution => {
  if (!referrer) return { source: 'direct', medium: 'none' };

  let host: string;
  try {
    host = new URL(referrer).hostname.toLowerCase();
  } catch {
    return { source: 'direct', medium: 'none' };
  }

  const normalizedCurrentHost = currentHost.toLowerCase();
  if (
    host === normalizedCurrentHost ||
    host === 'oraai.com' ||
    host.endsWith('.oraai.com') ||
    host === 'localhost' ||
    host === '127.0.0.1'
  ) {
    return { source: 'direct', medium: 'none' };
  }

  const strippedHost = stripReferrerPrefix(host);

  if (
    matchesDomain(strippedHost, 'chatgpt.com') ||
    matchesDomain(strippedHost, 'chat.openai.com') ||
    matchesDomain(strippedHost, 'perplexity.ai') ||
    matchesDomain(strippedHost, 'claude.ai') ||
    matchesDomain(strippedHost, 'gemini.google.com') ||
    matchesDomain(strippedHost, 'copilot.microsoft.com') ||
    matchesDomain(strippedHost, 'hisearch.ai')
  ) {
    return { source: strippedHost, medium: 'ai' };
  }

  if (
    /^google\.[^.]+$/.test(strippedHost) ||
    /^google\.(?:co|com)\.[^.]+$/.test(strippedHost) ||
    strippedHost === 'com.google.android.googlequicksearchbox'
  ) {
    return { source: 'google', medium: 'organic' };
  }

  const searchEngines: Array<[string, string]> = [
    ['bing.com', 'bing'],
    ['search.yahoo.com', 'yahoo'],
    ['search.yahoo.co.jp', 'yahoo'],
    ['duckduckgo.com', 'duckduckgo'],
    ['baidu.com', 'baidu'],
    ['ecosia.org', 'ecosia'],
    ['search.brave.com', 'brave'],
    ['coccoc.com', 'coccoc'],
    ['ask.com', 'ask'],
    ['search.aol.com', 'aol'],
    ['naver.com', 'naver'],
  ];

  const searchEngine = searchEngines.find(([domain]) =>
    matchesDomain(strippedHost, domain)
  );

  const yandexDomains = [
    'yandex.ru',
    'yandex.com',
    'yandex.com.tr',
    'yandex.by',
    'yandex.kz',
    'yandex.ua',
    'yandex.uz',
    'ya.ru',
  ];

  if (
    searchEngine ||
    yandexDomains.some((domain) => matchesDomain(strippedHost, domain))
  ) {
    return {
      source: searchEngine?.[1] ?? 'yandex',
      medium: 'organic',
    };
  }

  const socialNetworks: Array<[string, string]> = [
    ['facebook.com', 'facebook'],
    ['fb.com', 'facebook'],
    ['instagram.com', 'instagram'],
    ['tiktok.com', 'tiktok'],
    ['twitter.com', 'x'],
    ['x.com', 'x'],
    ['t.co', 'x'],
    ['linkedin.com', 'linkedin'],
    ['reddit.com', 'reddit'],
    ['youtube.com', 'youtube'],
    ['youtu.be', 'youtube'],
    ['pinterest.com', 'pinterest'],
    ['snapchat.com', 'snapchat'],
    ['threads.net', 'threads'],
    ['discord.com', 'discord'],
    ['groupme.com', 'groupme'],
  ];

  const socialNetwork = socialNetworks.find(([domain]) =>
    matchesDomain(strippedHost, domain)
  );

  if (socialNetwork) {
    return { source: socialNetwork[1], medium: 'social' };
  }

  if (
    matchesDomain(strippedHost, 'mail.google.com') ||
    matchesDomain(strippedHost, 'com.google.android.gm') ||
    matchesDomain(strippedHost, 'outlook.live.com') ||
    matchesDomain(strippedHost, 'outlook.office.com') ||
    matchesDomain(strippedHost, 'outlook.office365.com') ||
    matchesDomain(strippedHost, 'mail.yahoo.com')
  ) {
    return { source: strippedHost, medium: 'email' };
  }

  return { source: strippedHost, medium: 'referral' };
};

const sanitizeLandingPath = (value: string | null): string | undefined => {
  if (!value) return undefined;
  const trimmed = value.trim().split('?')[0];
  if (!trimmed.startsWith('/')) return undefined;
  const path = trimmed.replace(/\/+$/, '') || '/';
  return path.slice(0, 200);
};

const getCurrentLandingPath = (): string | undefined => {
  if (typeof window === 'undefined') return undefined;
  return sanitizeLandingPath(window.location.pathname);
};

export const inferAttributionFromReferrer = (): UserAttribution => {
  const inferred = classifyReferrer(
    typeof document === 'undefined' ? '' : document.referrer,
    typeof window === 'undefined' ? '' : window.location.hostname
  );

  return {
    utmSource: inferred.source,
    utmMedium: inferred.medium,
    landingPath: getCurrentLandingPath(),
    inferred: true,
    capturedAt: new Date().toISOString(),
  };
};

export const isSketchyPartnershipAttribution = (
  attribution?: { utmCampaign?: string | null } | null
) => {
  const campaign = pickString(attribution?.utmCampaign, true);
  return Boolean(campaign && SKETCHY_PARTNERSHIP_CAMPAIGNS.has(campaign));
};

const readCookie = (name: string): string | null => {
  if (typeof document === 'undefined') return null;

  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length !== 2) return null;
  return parts.pop()?.split(';').shift() ?? null;
};

const setCookie = (name: string, value: string, days: number) => {
  if (typeof document === 'undefined') return;

  const expires = new Date();
  expires.setDate(expires.getDate() + days);

  if (process.env.NODE_ENV === 'production') {
    document.cookie = `${name}=${value}; expires=${expires.toUTCString()}; Domain=.oraai.com; Path=/; SameSite=Lax; Secure`;
    return;
  }

  document.cookie = `${name}=${value}; expires=${expires.toUTCString()}; Path=/; SameSite=Lax`;
};

const hasAttribution = (attribution: UserAttribution | null | undefined) =>
  Boolean(
    attribution &&
    UTM_QUERY_KEYS.some((key) => {
      switch (key) {
        case 'utm_source':
          return attribution.utmSource;
        case 'utm_medium':
          return attribution.utmMedium;
        case 'utm_campaign':
          return attribution.utmCampaign;
        case 'utm_term':
          return attribution.utmTerm;
        case 'utm_content':
          return attribution.utmContent;
        default:
          return false;
      }
    })
  );

const fromUtmRecord = (utm: UTMRecord): UserAttribution | null => {
  const payload: UserAttribution = {
    utmSource: utm.utm_source,
    utmMedium: utm.utm_medium,
    utmCampaign: utm.utm_campaign,
    utmTerm: utm.utm_term,
    utmContent: utm.utm_content,
  };

  return hasAttribution(payload) ? payload : null;
};

export const getAttributionFromSearchParams = (
  searchParams: URLSearchParams
): UserAttribution | null => {
  const utm: UTMRecord = {};

  UTM_QUERY_KEYS.forEach((key) => {
    const raw =
      searchParams.get(key) ??
      searchParams.get(UTM_QUERY_PARAM_ALIASES[key].authKey);

    if (!raw) return;

    const normalized = normalizeValue(
      raw,
      UTM_QUERY_PARAM_ALIASES[key].shouldLowercase
    );

    if (!normalized) return;

    utm[key] = normalized;
  });

  const base = fromUtmRecord(utm);
  if (!base) return null;

  // Pricing and other in-app entry points forward the resolved attribution
  // to /auth as query params; keep the original landing path and the
  // inferred flag so a forwarded referrer inference is never re-read as an
  // explicit UTM.
  const landingPath = sanitizeLandingPath(
    searchParams.get('landingPath') ?? searchParams.get('landing_path')
  );

  const inferred = searchParams.get('inferred') === 'true';

  return {
    ...base,
    ...(landingPath ? { landingPath } : {}),
    ...(inferred ? { inferred: true } : {}),
  };
};

export const getAttributionFromCurrentUrl = (): UserAttribution | null => {
  if (typeof window === 'undefined') return null;
  return getAttributionFromSearchParams(
    new URLSearchParams(window.location.search)
  );
};

export const getStoredAttribution = (): UserAttribution | null => {
  const raw = readCookie('ora-attribution');
  if (!raw) return null;

  try {
    const parsed = JSON.parse(decodeURIComponent(raw)) as RawAttributionCookie;

    const normalized: UserAttribution = {
      utmSource: pickString(parsed.utmSource ?? parsed.utm_source, true),
      utmMedium: pickString(parsed.utmMedium ?? parsed.utm_medium, true),
      utmCampaign: pickString(parsed.utmCampaign ?? parsed.utm_campaign, true),
      utmTerm: pickString(parsed.utmTerm ?? parsed.utm_term),
      utmContent: pickString(parsed.utmContent ?? parsed.utm_content),
      capturedAt: pickString(parsed.capturedAt ?? parsed.captured_at),
      landingPath: pickString(parsed.landingPath ?? parsed.landing_path),
      inferred: parsed.inferred === true,
    };

    return hasAttribution(normalized) ? normalized : null;
  } catch {
    return null;
  }
};

export const persistAttributionCookie = (
  attribution: UserAttribution | null
) => {
  if (!hasAttribution(attribution)) return;

  const payload: UTMRecord & {
    captured_at: string;
    landing_path?: string;
    inferred?: true;
  } = {
    utm_source: attribution?.utmSource,
    utm_medium: attribution?.utmMedium,
    utm_campaign: attribution?.utmCampaign,
    utm_term: attribution?.utmTerm,
    utm_content: attribution?.utmContent,
    captured_at: attribution?.capturedAt ?? new Date().toISOString(),
    landing_path: attribution?.landingPath,
    ...(attribution?.inferred ? { inferred: true } : {}),
  };

  setCookie('ora-attribution', encodeURIComponent(JSON.stringify(payload)), 90);
};

const deleteCookie = (name: string) => {
  if (typeof document === 'undefined') return;

  const expired = 'expires=Thu, 01 Jan 1970 00:00:00 GMT';
  // In production the tracking cookies are written on Domain=.oraai.com, so the
  // delete must match that domain to actually remove them. We also clear the
  // host-only variant to cover dev and any non-domain writes.
  if (process.env.NODE_ENV === 'production') {
    document.cookie = `${name}=; ${expired}; Domain=.oraai.com; Path=/; SameSite=Lax; Secure`;
  }
  document.cookie = `${name}=; ${expired}; Path=/; SameSite=Lax`;
};

// Clears every piece of persisted referral/cohort tracking that can make the
// discount banner appear. `ora-attribution` and `ora-cohort` are first-party
// cookies on `.oraai.com`, so clearing them here also stops the banner on the
// marketing site (oraai.com), not just the app.
export const clearDiscountTracking = () => {
  deleteCookie('ora-attribution');
  deleteCookie('ora-cohort');

  try {
    sessionStorage.removeItem('ora-refId');
    sessionStorage.removeItem('ora-cohort');
    localStorage.removeItem('ora-cohort');
    localStorage.removeItem('ora-cohort-link');
  } catch {
    // Storage may be unavailable (private mode); the cookie clear above is the
    // part that actually drives the banner.
  }
};

export const resolveFirstTouchAttribution = (): {
  attribution: UserAttribution | null;
  origin: AttributionOrigin;
} => {
  const cookieAttribution = getStoredAttribution();
  if (cookieAttribution && !cookieAttribution.inferred) {
    return { attribution: cookieAttribution, origin: 'cookie' };
  }

  const urlAttribution = getAttributionFromCurrentUrl();
  if (urlAttribution && !urlAttribution.inferred) {
    const attribution = {
      ...urlAttribution,
      landingPath: urlAttribution.landingPath ?? getCurrentLandingPath(),
      capturedAt: new Date().toISOString(),
    };

    persistAttributionCookie(attribution);
    return {
      attribution,
      origin: 'url',
    };
  }

  if (cookieAttribution) {
    return { attribution: cookieAttribution, origin: 'cookie' };
  }

  if (urlAttribution) {
    // A forwarded inference (e.g. Pricing → /auth) stays inferred.
    const attribution = {
      ...urlAttribution,
      landingPath: urlAttribution.landingPath ?? getCurrentLandingPath(),
      capturedAt: new Date().toISOString(),
      inferred: true,
    };

    persistAttributionCookie(attribution);
    return { attribution, origin: 'url' };
  }

  if (typeof window === 'undefined') {
    return { attribution: null, origin: 'none' };
  }

  const attribution = inferAttributionFromReferrer();
  persistAttributionCookie(attribution);
  return { attribution, origin: 'referrer' };
};

export const resolveCurrentVisitAttribution = (): {
  attribution: UserAttribution | null;
  origin: AttributionOrigin;
} => {
  const urlAttribution = getAttributionFromCurrentUrl();
  if (urlAttribution && !urlAttribution.inferred) {
    return {
      attribution: {
        ...urlAttribution,
        landingPath: urlAttribution.landingPath ?? getCurrentLandingPath(),
        capturedAt: new Date().toISOString(),
      },
      origin: 'url',
    };
  }

  const cookieAttribution = getStoredAttribution();
  if (cookieAttribution) {
    return { attribution: cookieAttribution, origin: 'cookie' };
  }

  if (urlAttribution) {
    return {
      attribution: {
        ...urlAttribution,
        landingPath: urlAttribution.landingPath ?? getCurrentLandingPath(),
        capturedAt: new Date().toISOString(),
        inferred: true,
      },
      origin: 'url',
    };
  }

  if (typeof window !== 'undefined') {
    return {
      attribution: inferAttributionFromReferrer(),
      origin: 'referrer',
    };
  }

  return { attribution: null, origin: 'none' };
};

export const resolveMarketingOfferKey = (): string | null => {
  const urlAttribution = getAttributionFromCurrentUrl();

  const urlCampaign = urlAttribution?.inferred
    ? undefined
    : urlAttribution?.utmCampaign?.trim().toLowerCase();

  if (urlCampaign) return urlCampaign;

  const storedAttribution = getStoredAttribution();
  // A referrer-inferred cookie can never carry an offer, whatever it contains.
  if (storedAttribution?.inferred) return null;
  const storedCampaign = storedAttribution?.utmCampaign?.trim().toLowerCase();
  return storedCampaign || null;
};

export const captureFirstTouchAttributionFromCurrentUrl = () => {
  return resolveFirstTouchAttribution().attribution;
};

export const toPosthogUtmProperties = (
  attribution: UserAttribution | null
): Record<string, string> => {
  if (!attribution) return {};

  const props: Record<string, string> = {};
  if (attribution.utmSource) props.utm_source = attribution.utmSource;
  if (attribution.utmMedium) props.utm_medium = attribution.utmMedium;
  if (attribution.utmCampaign) props.utm_campaign = attribution.utmCampaign;
  if (attribution.utmTerm) props.utm_term = attribution.utmTerm;
  if (attribution.utmContent) props.utm_content = attribution.utmContent;
  props.attribution_inferred = attribution.inferred ? 'true' : 'false';
  if (attribution.landingPath) props.landing_path = attribution.landingPath;
  return props;
};

export const toAuthAttributionPayload = (
  attribution: UserAttribution | null
): Partial<UserAttribution> => {
  if (!attribution) return {};

  return {
    utmSource: attribution.utmSource,
    utmMedium: attribution.utmMedium,
    utmCampaign: attribution.utmCampaign,
    utmTerm: attribution.utmTerm,
    utmContent: attribution.utmContent,
    landingPath: attribution.landingPath,
    // Inferred attribution is only trusted at account creation; the backend
    // must not stamp it onto an existing account as a first touch.
    ...(attribution.inferred ? { inferred: true } : {}),
  };
};

export const appendAttributionToAuthQueryParams = (
  params: URLSearchParams,
  attribution: UserAttribution | null
) => {
  if (!attribution) return;

  if (attribution.utmSource) params.set('utmSource', attribution.utmSource);
  if (attribution.utmMedium) params.set('utmMedium', attribution.utmMedium);
  if (attribution.utmCampaign) {
    params.set('utmCampaign', attribution.utmCampaign);
  }
  if (attribution.utmTerm) params.set('utmTerm', attribution.utmTerm);
  if (attribution.utmContent) params.set('utmContent', attribution.utmContent);
  if (attribution.landingPath)
    params.set('landingPath', attribution.landingPath);
  if (attribution.inferred) params.set('inferred', 'true');
};

const hasAuthAttribution = (payload: Partial<UserAttribution>) =>
  Boolean(
    payload.utmSource ||
    payload.utmMedium ||
    payload.utmCampaign ||
    payload.utmTerm ||
    payload.utmContent ||
    payload.landingPath
  );

/**
 * Fills in the current visit's attribution when a caller (e.g. the /unlock
 * or webinar signup flows) did not resolve one itself, so every auth entry
 * point reports the same source/medium/landing path.
 */
export const withDefaultAuthAttribution = <T extends object>(
  payload: T
): T & Partial<UserAttribution> => {
  if (hasAuthAttribution(payload as Partial<UserAttribution>)) return payload;
  const { attribution } = resolveCurrentVisitAttribution();
  return { ...payload, ...toAuthAttributionPayload(attribution) };
};
